import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { BannerContext } from '../contexts/BannerContext';
import { PusherContext } from '../providers/PusherProvider';

const STORAGE_KEY = 'pt.tradeHistorySyncJob';

function readStoredJob() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.jobId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredJob(job) {
  try {
    if (!job?.jobId) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(job));
  } catch {
    /* ignore quota / private mode */
  }
}

function clearStoredJob(jobId) {
  try {
    const current = readStoredJob();
    if (!jobId || current?.jobId === jobId) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function successCopy(mode, result) {
  if (mode === 'deep' || result?.forceDeepRebuild) return 'Deep rebuild complete';
  if (mode === 'upload') return 'Upload succeeded';
  return 'Rebuild complete';
}

/**
 * Tracks trade-history sync jobs across modal dismiss / remount.
 * Persists jobId in sessionStorage; always toasts on complete/fail via poll + Pusher.
 */
export default function useTradeHistorySyncJobMonitor({
  authToken,
  backendUrl,
  onSuccess,
} = {}) {
  const { user } = useContext(AuthContext) || {};
  const { showMessage } = useContext(BannerContext) || {};
  const pusher = useContext(PusherContext);
  const [activeJob, setActiveJob] = useState(() => readStoredJob());
  const settlingRef = useRef(false);
  const activeJobRef = useRef(activeJob);
  activeJobRef.current = activeJob;

  const settleJob = useCallback(
    (payload) => {
      const job = activeJobRef.current;
      if (!job?.jobId || !payload?.jobId || payload.jobId !== job.jobId) return;
      if (settlingRef.current) return;
      settlingRef.current = true;

      const mode = job.mode || payload.mode;
      if (payload.status === 'completed') {
        const result = payload.result || {};
        const apiIssue = Array.isArray(result.issues)
          ? result.issues.find((i) => i?.type === 'api_fetch_error')
          : null;
        showMessage?.(successCopy(mode, result), 'success', false);
        if (apiIssue?.message) {
          showMessage?.(apiIssue.message, 'warning', false);
        }
        onSuccess?.(result);
      } else if (payload.status === 'failed') {
        showMessage?.(payload.error || 'Sync failed', 'danger', false);
      }

      clearStoredJob(job.jobId);
      setActiveJob(null);
      settlingRef.current = false;
    },
    [onSuccess, showMessage]
  );

  const trackJob = useCallback((job) => {
    if (!job?.jobId) return;
    const next = {
      jobId: job.jobId,
      mode: job.mode || 'rebuild',
      accountId: job.accountId || null,
      startedAt: new Date().toISOString(),
    };
    settlingRef.current = false;
    writeStoredJob(next);
    setActiveJob(next);
  }, []);

  // Pusher
  useEffect(() => {
    if (!pusher || !user?.id) return undefined;
    const channelName = `user-${user.id}-alerts`;
    let channel = pusher.channel(channelName);
    if (!channel) channel = pusher.subscribe(channelName);

    const onSync = (data) => {
      const job = activeJobRef.current;
      if (!job?.jobId || data?.jobId !== job.jobId) return;
      if (data.status === 'completed' || data.status === 'failed') {
        settleJob(data);
      }
    };
    channel.bind('trade-history-sync', onSync);
    return () => {
      channel.unbind('trade-history-sync', onSync);
    };
  }, [pusher, user?.id, settleJob]);

  // HTTP poll fallback (always — Pusher may be missing or event missed)
  useEffect(() => {
    if (!activeJob?.jobId || !authToken || !backendUrl) return undefined;
    let cancelled = false;

    const tick = async () => {
      const jobId = activeJobRef.current?.jobId;
      if (!jobId || cancelled) return;
      try {
        const res = await fetch(
          `${backendUrl}/performance/trades/sync-trade-history/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || jobId !== activeJobRef.current?.jobId) return;
        if (data.status === 'completed' || data.status === 'failed') {
          settleJob({
            jobId,
            status: data.status,
            mode: data.mode,
            result: data.result,
            error: data.error,
          });
        }
      } catch {
        /* ignore transient poll errors */
      }
    };

    const t = setInterval(tick, 2500);
    tick();
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [activeJob?.jobId, authToken, backendUrl, settleJob]);

  return { activeJob, trackJob };
}

export { STORAGE_KEY as TRADE_HISTORY_SYNC_JOB_KEY, readStoredJob, clearStoredJob };
